import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";

import { SearchSuggestion, SearchInputType } from "../../model";

/**
 * Returns the Material icon name for a given search input type.
 * @param type SearchInputType
 * @returns Icon name as string
 */
function getSuggestionIcon(type: SearchInputType): string {
  switch (type) {
    case SearchInputType.COORDINATE:
      return "place";
    case SearchInputType.WKT_GEOMETRY:
      return "category";
    case SearchInputType.GOOGLE_PLACE:
      return "location_on";
    case SearchInputType.TEXT:
    default:
      return "search";
  }
}

/**
 * Returns the display label for a given search input type.
 * @param type SearchInputType
 * @returns Label as string
 */
function getSuggestionLabel(type: SearchInputType): string {
  switch (type) {
    case SearchInputType.COORDINATE:
      return "Coordinate";
    case SearchInputType.WKT_GEOMETRY:
      return "Geometry";
    case SearchInputType.GOOGLE_PLACE:
      return "Address";
    case SearchInputType.TEXT:
    default:
      return "Text";
  }
}

/**
 * Suggestion panel component for displaying search suggestions
 *
 * Displays suggestion with:
 * - Type-specific icon and color
 * - Confidence level badge
 * - History usage indicators
 * - Clickable interface
 */
@Component({
  selector: "app-suggestion-panel",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./suggestion-panel.component.html",
  styleUrls: ["./suggestion-panel.component.scss"],
  standalone: true,
  imports: [CommonModule, MatIconModule],
})
export class SuggestionPanelComponent {
  // ========================================
  // INPUTS & OUTPUTS
  // ========================================

  /** Suggestion data to display */
  @Input({ required: true }) suggestion!: SearchSuggestion;

  /** Whether this suggestion is highlighted via keyboard navigation */
  @Input() isActive = false;

  /** Click event when suggestion is selected */
  @Output() suggestionClick = new EventEmitter<SearchSuggestion>();

  // ========================================
  // UI HELPER METHODS
  // ========================================

  /**
   * Handle suggestion click
   */
  onSuggestionClick(): void {
    this.suggestionClick.emit(this.suggestion);
  }

  /**
   * Get icon name for suggestion type
   */
  getSuggestionIcon(type: SearchInputType): string {
    return getSuggestionIcon(type);
  }

  /**
   * Get display label for suggestion type
   */
  getSuggestionLabel(type: SearchInputType): string {
    return getSuggestionLabel(type);
  }

  /**
   * Get accessibility label for the suggestion
   */
  getAriaLabel(): string {
    const typeLabel = this.getSuggestionLabel(this.suggestion.type);
    const confidenceText = this.suggestion.confidence > 0 ? `, ${this.suggestion.confidence}% confidence` : "";
    const usageText = this.suggestion.lastUsed ? `, used ${this.suggestion.usageCount || 1} times` : "";

    return `${typeLabel} suggestion: ${this.suggestion.text}${confidenceText}${usageText}`;
  }
}
